<template>
    <section
        class="flex h-full min-h-0 flex-col bg-surface"
        :aria-label="artifact ? t('artifacts.panel.accessibleName', { title, version: viewedVersion }) : t('artifacts.document')"
        data-testid="artifact-panel">
        <!-- Toolbar: title · type on the left, actions on the right (modelled on Claude's artifact pane). -->
        <div class="flex items-center gap-2 border-b border-border px-4 py-2">
            <div class="flex min-w-0 flex-1 items-center gap-2">
                <BaseDropdownMenu v-if="artifact && artifact.version_count > 1" :sections="versionSections" content-class="min-w-32" @select="handleVersionSelect">
                    <template #trigger>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            class="inline-flex min-w-0 max-w-full items-center"
                            :aria-label="t('artifacts.panel.versionsOf', { title, version: viewedVersion })"
                            data-testid="artifact-version-trigger">
                            <span class="nadia-chat-bold min-w-0 truncate text-ink" data-testid="artifact-panel-title">{{ title }}</span>
                            <span class="nadia-chat-bold shrink-0 text-ink tabular-nums">.{{ t("artifacts.panel.versionOption", { version: viewedVersion }) }}</span>
                            <i class="bi bi-chevron-down ml-2 shrink-0 text-ink-muted" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template v-for="n in artifact.version_count" :key="n" #[`item-version-${n}`]="{ item }">
                        <span class="flex-1">{{ item.label }}</span>
                        <i v-if="n === viewedVersion" class="bi bi-check-lg shrink-0" aria-hidden="true"></i>
                    </template>
                </BaseDropdownMenu>
                <h2 v-else class="nadia-chat-bold truncate text-ink" data-testid="artifact-panel-title">{{ title }}</h2>
            </div>

            <div class="flex shrink-0 items-center gap-1">
                <div v-if="artifact" class="flex items-center gap-1">
                    <BaseTooltip>
                        <template #trigger>
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :aria-label="copied ? t('artifacts.panel.copied') : t('artifacts.panel.copyDocument')"
                                :disabled="exportsLocked"
                                data-testid="artifact-copy"
                                @click="copyDocument">
                                <i class="bi" :class="copied ? 'bi-check-lg' : 'bi-copy'" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                        <template #content>{{ copied ? t("artifacts.panel.copied") : t("artifacts.panel.copyDocument") }}</template>
                    </BaseTooltip>
                    <BaseDropdownMenu :sections="menuSections" :aria-label="t('artifacts.panel.actions')" @select="handleMenuSelect">
                        <template #trigger>
                            <BaseTooltip>
                                <template #trigger>
                                    <BaseButton
                                        theme="tertiary"
                                        size="sm"
                                        icon-only
                                        :aria-label="t('artifacts.panel.moreActions')"
                                        :disabled="exportsLocked"
                                        data-testid="artifact-menu">
                                        <i class="bi bi-download" aria-hidden="true"></i>
                                    </BaseButton>
                                </template>
                                <template #content>{{ t("artifacts.panel.moreActions") }}</template>
                            </BaseTooltip>
                        </template>
                    </BaseDropdownMenu>
                </div>

                <BaseTooltip v-if="!hideExpand">
                    <template #trigger>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :aria-label="expanded ? t('artifacts.panel.shrink') : t('artifacts.panel.expand')"
                            :aria-pressed="expanded"
                            data-testid="artifact-panel-expand"
                            @click="emit('toggle-expand')">
                            <i class="bi" :class="expanded ? 'bi-arrows-angle-contract' : 'bi-arrows-angle-expand'" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content>{{ expanded ? t("artifacts.panel.shrink") : t("artifacts.panel.expand") }}</template>
                </BaseTooltip>
                <BaseTooltip v-if="!hideClose">
                    <template #trigger>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            class="ml-3"
                            :aria-label="t('artifacts.panel.close')"
                            data-testid="artifact-panel-close"
                            @click="emit('close')">
                            <i class="bi bi-x-lg" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content>{{ t("artifacts.panel.close") }}</template>
                </BaseTooltip>
            </div>
        </div>

        <!-- Shown only while stepping back through history. -->
        <div
            v-if="artifact && !isCurrent"
            class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border bg-surface-sunken px-4 py-2 details text-ink-muted"
            data-testid="artifact-version-older">
            <span class="flex min-w-0 flex-wrap items-center">
                <span class="shrink-0">{{ t("artifacts.panel.olderVersion") }}</span>
                <span v-if="artifact.version.change_summary" class="truncate before:mx-2 before:content-['·']">{{ artifact.version.change_summary }}</span>
                <span class="shrink-0 before:mx-2 before:content-['·']">{{ formatValidDate(artifact.version.created_at, "datetime") }}</span>
            </span>
            <BaseButton theme="tertiary" size="sm" data-testid="artifact-version-latest" @click="showLatest">
                {{ t("artifacts.panel.viewLatest") }}
            </BaseButton>
        </div>

        <!-- Document -->
        <div class="min-h-0 flex-1 overflow-y-auto">
            <div class="mx-auto w-full max-w-prose px-6 py-10 sm:px-10">
                <p v-if="loading && !artifact" class="details text-ink-muted">{{ t("artifacts.panel.loading") }}</p>
                <p v-else-if="error" role="alert" class="details text-status-error" data-testid="artifact-panel-error">{{ error }}</p>
                <div v-else-if="artifact" ref="bodyEl" class="doc text-ink" :class="{ 'opacity-60': loading }" data-testid="artifact-body">
                    <MarkdownText :text="artifact.version.content" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useClipboard } from "@vueuse/core";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { formatValidDate } from "~vue/utils/formatDate";
import { logError } from "~vue/utils/logError";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { type Artifact, artifactFileName, fetchArtifact, fetchArtifactVersion } from "./artifacts";

const props = withDefaults(
    defineProps<{
        artifactId: string;
        // Bumped by the parent whenever a new version of the open artifact is saved, so the
        // panel re-fetches and jumps to the latest body even though the id is unchanged.
        refreshKey?: number;
        // The mobile dialog supplies its own close control and has no wide mode.
        hideClose?: boolean;
        hideExpand?: boolean;
        expanded?: boolean;
    }>(),
    { refreshKey: 0, hideClose: false, hideExpand: false, expanded: false },
);

const emit = defineEmits<{ (e: "close"): void; (e: "toggle-expand"): void }>();
const { t } = useI18n();

const artifact = ref<Artifact | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const bodyEl = useTemplateRef<HTMLElement>("bodyEl");
const viewedVersion = computed(() => artifact.value?.version.version_number ?? 0);
const isCurrent = computed(() => artifact.value !== null && viewedVersion.value === artifact.value.current_version_number);
const title = computed(() => artifact.value?.title || t("artifacts.document"));

// While another version loads (or its load failed) `artifact` still holds the previous one, which must not be
// what Copy / Download / Print export.
const exportsLocked = computed(() => loading.value || error.value !== null);
const { copy, copied } = useClipboard({ copiedDuring: 2000 });
const copyDocument = () => {
    if (artifact.value && !exportsLocked.value) {
        void copy(artifact.value.version.content);
    }
};

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "export",
        items: [
            { id: "download", type: "button", label: t("artifacts.panel.downloadMarkdown"), icon: "bi bi-download", testId: "artifact-download" },
            { id: "print", type: "button", label: t("artifacts.panel.printPdf"), icon: "bi bi-printer", testId: "artifact-print" },
        ],
    },
]);

// All versions, newest first; the one on screen carries a check. Opened from the chevron by the title.
const versionSections = computed<DropdownMenuSection[]>(() => {
    const art = artifact.value;
    if (!art) {
        return [];
    }
    const items: DropdownMenuItem[] = [];
    for (let n = art.version_count; n >= 1; n--) {
        items.push({
            id: `version-${n}`,
            type: "button",
            label: t("artifacts.panel.versionOption", { version: n }),
            ariaCurrent: n === viewedVersion.value,
            testId: `artifact-version-option-${n}`,
        });
    }
    return [{ id: "versions", items }];
});

const handleVersionSelect = (item: DropdownMenuItem) => {
    const art = artifact.value;
    const target = Number(item.id.replace("version-", ""));
    if (!art || Number.isNaN(target) || target === viewedVersion.value) {
        return;
    }
    // Selecting the live version re-reads it through the canonical "latest" endpoint.
    void load(target === art.current_version_number ? null : target);
};

const downloadDocument = () => {
    if (!artifact.value || exportsLocked.value) {
        return;
    }
    const blob = new Blob([artifact.value.version.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = artifactFileName(artifact.value);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
};

// Print the rendered (already sanitized) document on its own, without the chat around it; the
// user picks "Save as PDF" in the browser's print dialog.
const printDocument = () => {
    const html = bodyEl.value?.innerHTML;
    if (!artifact.value || !html || exportsLocked.value) {
        return;
    }
    const printWindow = globalThis.open("", "_blank", "noopener=false");
    if (!printWindow) {
        return;
    }
    printWindow.document.title = title.value;
    printWindow.document.body.innerHTML = html;
    printWindow.document.body.style.fontFamily = globalThis.getComputedStyle(document.body).fontFamily;
    printWindow.document.body.style.margin = "2rem auto";
    printWindow.document.body.style.maxWidth = "42rem";
    printWindow.focus();
    printWindow.print();
};

const handleMenuSelect = (item: DropdownMenuItem) => {
    if (item.id === "download") {
        downloadDocument();
    } else if (item.id === "print") {
        printDocument();
    }
};

// Only the newest request may write state: clicking two cards (or stepping versions) quickly
// must never leave an older, slower response on screen.
let requestSeq = 0;

const load = async (versionNumber: number | null) => {
    const seq = ++requestSeq;
    loading.value = true;
    error.value = null;
    try {
        const loaded = versionNumber === null ? await fetchArtifact(props.artifactId) : await fetchArtifactVersion(props.artifactId, versionNumber);
        if (seq === requestSeq) {
            artifact.value = loaded;
        }
    } catch (err) {
        if (seq === requestSeq) {
            const e = err instanceof Error ? err : new Error(String(err));
            error.value = e.message;
            logError(e);
        }
    } finally {
        if (seq === requestSeq) {
            loading.value = false;
        }
    }
};

const showLatest = () => {
    void load(null);
};

watch(
    () => [props.artifactId, props.refreshKey] as const,
    () => {
        // Clear stale content when switching to a different artifact so the old body doesn't
        // flash while the new one loads. A refresh (new version saved) jumps back to the latest.
        if (artifact.value && artifact.value.id !== props.artifactId) {
            artifact.value = null;
        }
        void load(null);
    },
    { immediate: true },
);
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

/* Body copy is Inter 14px (Tonal: Small App) and titles are Inter too, keeping the
   shared `[data-marked]` heading sizes (chat hierarchy) and bold weight — only the
   serif face is overridden. `markdown.css` styles `[data-marked]` descendants with
   UNLAYERED rules (`font: inherit`), which outrank any `@layer components` rule
   regardless of specificity, so these font rules must stay unlayered too — then
   `.doc[data-v] p` (0,2,1) wins over `[data-marked] p` (0,1,1). Block spacing is left
   to the shared `[data-marked]` flex gap, so the document spaces like Nadia's chat. */
.doc :deep(p),
.doc :deep(ul),
.doc :deep(ol),
.doc :deep(li),
.doc :deep(blockquote) {
    @apply body-small;
}
.doc :deep(h1),
.doc :deep(h2),
.doc :deep(h3),
.doc :deep(h4),
.doc :deep(h5),
.doc :deep(h6) {
    @apply font-sans;
}

@layer components {
    /* Extra breathing room above each section title, on top of the shared chat gap.
       The very first block sits flush against the pane's own top padding. */
    .doc :deep(h1),
    .doc :deep(h2),
    .doc :deep(h3),
    .doc :deep(h4),
    .doc :deep(h5),
    .doc :deep(h6) {
        @apply mt-6;
    }
    .doc :deep([data-marked] > :first-child) {
        @apply mt-0;
    }

    .doc :deep(blockquote) {
        @apply border-l-2 border-border pl-4 text-ink-muted;
    }
    .doc :deep(code) {
        @apply rounded bg-surface-sunken px-1 py-1 font-mono text-sm;
    }
}
</style>
